import React, { useState, useEffect } from 'react';
import { Save, Home, Plus, Minus, RotateCcw } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Coefficient {
  id: string;
  type: 'house';
  name: string;
  value: number;
  description: string;
  default_value: number;
}

const MIN_VALUE = -20;
const MAX_VALUE = 20;
const STEP = 0.5;

// Group definitions for organizing coefficients
const COEFFICIENT_GROUPS = [
  {
    id: 'terrain',
    title: 'Terrains / Jardin / Terrasse',
    coefficients: ['terrain', 'jardin', 'terrasse']
  },
  {
    id: 'amenagements',
    title: 'Aménagements',
    coefficients: ['cave', 'garage', 'abri_jardin', 'mitoyennete', 'parking']
  },
  {
    id: 'toiture',
    title: 'Toiture / Isolation / Accès',
    coefficients: [
      'toiture_tuiles', 'toiture_ardoises', 'toiture_zinc', 'toiture_terrasse',
      'isolation_thermique', 'isolation_phonique', 'isolation_double', 'isolation_aucune',
      'acces_independant', 'acces_partage'
    ]
  },
  {
    id: 'equipements',
    title: 'Équipements énergétiques',
    coefficients: [
      'energie_pompe_chaleur', 'energie_panneaux_solaires', 
      'energie_poele_bois', 'energie_chaudiere_gaz'
    ]
  },
  {
    id: 'chauffage',
    title: 'Chauffage',
    coefficients: [
      'chauffage_nature_electrique', 'chauffage_nature_gaz', 'chauffage_nature_fioul',
      'chauffage_nature_bois', 'chauffage_nature_pompe_chaleur',
      'chauffage_type_individuel', 'chauffage_type_collectif'
    ]
  },
  {
    id: 'etat',
    title: 'États du bien',
    coefficients: [
      'etat_general', 'etat_toiture', 'etat_facade', 
      'etat_menuiseries', 'etat_sols'
    ]
  },
  {
    id: 'age',
    title: 'Âge de la maison',
    coefficients: ['age_maison']
  },
  {
    id: 'exposition',
    title: 'Exposition',
    coefficients: [
      'exposition_nord', 'exposition_sud', 'exposition_est_ouest',
      'exposition_nord_est_ouest', 'exposition_sud_est_ouest'
    ]
  }
];

export default function HouseEstimationCoefficients() {
  const [coefficients, setCoefficients] = useState<Coefficient[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchCoefficients();
  }, []);

  const fetchCoefficients = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/house-coefficients');
      
      if (!response.ok) {
        throw new Error('Failed to fetch house coefficients');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch house coefficients');
      }
      
      setCoefficients(result.data || []);
    } catch (error) {
      console.error('Error fetching house coefficients:', error);
      toast.error('Erreur lors du chargement des coefficients');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (id: string, increment: boolean) => {
    setCoefficients(prev =>
      prev.map(coef => {
        if (coef.id === id) {
          const newValue = increment 
            ? Math.min(coef.value + STEP, MAX_VALUE)
            : Math.max(coef.value - STEP, MIN_VALUE);
          return { ...coef, value: Number(newValue.toFixed(2)) };
        }
        return coef;
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/house-coefficients/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coefficients }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update coefficients');
      }

      toast.success('Coefficients mis à jour avec succès');
    } catch (error) {
      console.error('Error saving coefficients:', error);
      toast.error('Erreur lors de la sauvegarde des coefficients');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await fetch('/api/house-coefficients/reset', {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset coefficients');
      }

      const result = await response.json();
      setCoefficients(result.data || []);
      toast.success('Coefficients réinitialisés avec succès');
    } catch (error) {
      console.error('Error resetting coefficients:', error);
      toast.error('Erreur lors de la réinitialisation des coefficients');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const renderCoefficient = (coef: Coefficient) => (
    <div key={coef.id} className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {coef.description}
      </label>
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => handleValueChange(coef.id, false)}
          disabled={coef.value <= MIN_VALUE}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="w-20 text-center py-2 bg-gray-50 rounded-lg font-medium">
          {typeof coef.value === 'number' ? coef.value.toFixed(2) : coef.value}
        </div>
        <button
          type="button"
          onClick={() => handleValueChange(coef.id, true)}
          disabled={coef.value >= MAX_VALUE}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // Group coefficients by their category
  const renderCoefficientGroups = () => {
    return COEFFICIENT_GROUPS.map(group => {
      const groupCoefficients = coefficients.filter(coef => 
        group.coefficients.includes(coef.name)
      );
      
      if (groupCoefficients.length === 0) return null;
      
      return (
        <div key={group.id} className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{group.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groupCoefficients.map(renderCoefficient)}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="space-y-8">
      {/* House Coefficients */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <Home className="h-6 w-6 text-indigo-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">
              Coefficients Maison
            </h2>
          </div>
        </div>
        <div className="p-6">
          {renderCoefficientGroups()}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={handleReset}
          disabled={resetting}
          className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {resetting ? 'Réinitialisation...' : 'Remettre valeurs par défaut'}
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
}